/**
 * Shared listing field-requirements types and constants.
 *
 * Single source of truth for what's required vs optional in each phase of the
 * listing lifecycle (draft, publish/submit). Consumed by the wizard, the
 * edit page and the API.
 */

export const PHOTO_MIN = 5;
export const PHOTO_MAX = 20;
export const DESCRIPTION_MIN = 100;

/**
 * Cloudflare Stream video uploads (task: video listings v1).
 *
 * Hosts may attach up to {@link VIDEO_MAX} short videos to a listing in
 * addition to their photos. Videos live in `cars_videos` (separate from
 * `cars_images`) so the hero-image picker, primary-photo invariants and
 * photo-reorder RPC stay untouched. Moderation follows the same
 * trust-hosts-flag-on-report model as photos for v1.
 *
 * Caps mirrored on:
 *   - client (UI rejects oversized files before mint)
 *   - server (Stream API call sets maxDurationSeconds)
 *   - Cloudflare (max duration enforced at ingest by Stream itself)
 */
export const VIDEO_MAX = 2;
export const VIDEO_MAX_DURATION_SECONDS = 60;
export const VIDEO_MAX_SIZE_BYTES = 200 * 1024 * 1024; // 200 MB
export const VIDEO_ACCEPTED_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const;
export const YEAR_MIN = 1900;
/**
 * Hard upper bound on listing mileage (task #1595). Reality check:
 * a 1960s daily-driver classic with 600,000 mi is plausible; the
 * highest-mileage road-going vehicle on record is just over 3M mi.
 * 1.5M gives ample headroom for high-mileage classics while rejecting
 * obvious garbage / overflow attempts that would otherwise pollute
 * search facets and analytics surfaces.
 */
export const MILEAGE_MAX = 1_500_000;
/**
 * Upper bound on listing model year (task #1595). `+1` so hosts can
 * pre-list pre-orders / next-model-year pre-production cars without
 * tripping the gate the moment Jan 1 rolls over. Any year above this
 * is rejected as a typo / facet-pollution attempt (e.g. `9999`).
 */
export function getYearMax(now: Date = new Date()): number {
  return now.getUTCFullYear() + 1;
}

/** Aliases accepted from clients/payloads. The canonical name is on the right. */
export const FIELD_ALIASES: Readonly<Record<string, string>> = {
  colour: "color",
  convertible: "is_convertible",
  air_conditioning: "has_aircon",
  left_hand_drive: "is_left_hand_drive",
  body_style: "category",
  car_history: "history",
  no_smoking: "nonsmoking",
  mileage_overage_rate: "overage_rate_per_mile",
  mileage_limit_miles: "mileage_limit_per_day",
  rental_driving_min_duration: "driving_rental_min_duration",
};

export type ValidationError = { field: string; message: string; severity?: "error" | "warning"; code?: string };

export interface PriceBounds {
  dailyMin: number;
  dailyMax: number;
  hourlyMin: number;
  hourlyMax: number;
}

/**
 * Realized booking comparable — the daily rate (`rental_fee / nights`) a
 * past guest actually paid for a comparable car, in their billing currency.
 * Task #1590 turns these into a third pricing signal alongside the existing
 * asking-price comparables and valuation anchor.
 */
export interface BookingComparable {
  /** Daily rate in `currency` (rental_fee / number-of-days). */
  dailyRate: number;
  /** ISO 4217 currency code, e.g. `USD`, `GBP`, `EUR`. */
  currency: string;
}

export interface SuggestedPricingInput {
  /** Sorted (or unsorted) array of comparable active-listing daily rates. */
  comparables?: readonly number[];
  /**
   * Realized daily rates from past completed bookings of similar cars
   * (same make / country / year ±7 window as `comparables`). The blender
   * filters these to the segment matching `countryCode`'s currency, drops
   * outliers, and uses the median as a third signal in the base-rate blend.
   * When empty, behaviour is identical to today (asking + valuation only).
   * Task #1590.
   */
  bookingComparables?: readonly BookingComparable[];
  /** Insured / estimated market value of the vehicle in the local currency. */
  insuredValue?: number | null;
  /** ISO country code (e.g. `GB`, `US`) — drives the per-currency floor. */
  countryCode?: string | null;
  /** `condition` enum on the cars row (1 = excellent … 4 = fair). */
  condition?: number | null;
  /** City name — premium-market list nudges the rate up by 12%. */
  city?: string | null;
  /** Total APEX standout score (0–100). */
  apexScore?: number | null;
  /** Number of completed trips — used as a soft demand signal. */
  completedBookings?: number | null;
  /** Optional make label, surfaced in factor descriptions. */
  make?: string | null;
  /**
   * Broader same-make / same-era market median (asking daily rate, local
   * currency) used as the classic-car fallback anchor when the listing has
   * no insured value, no realized bookings and too few close comparables to
   * form a `platformMedian`. Computed by the API loader from a wider query
   * than `comparables`. When absent/≤0 the blender falls back to the flat
   * per-currency category default. Task #4615 step 4.
   */
  segmentMedianDaily?: number | null;
}

export interface PricingFactor {
  factor: string;
  impact: string;
  detail: string;
}

export interface SuggestedPricing {
  /** The single number we tell the host to charge per day for self-drive. */
  recommendedDaily: number;
  /** Per-rental-type recommendation lines shown under each input. */
  recommendedDailyEvent: number;
  recommendedHourlyChauffeured: number;
  /** Internal floors. Only `minHourlyChauffeured` becomes a host-facing error. */
  minDailySelfDrive: number;
  minDailyEvent: number;
  minHourlyChauffeured: number;
  /** Internal ceilings. Above-ceiling triggers an admin flag, no host message. */
  maxDailySelfDrive: number;
  maxDailyEvent: number;
  maxHourlyChauffeured: number;
  confidence: "high" | "medium" | "low";
  factors: PricingFactor[];
  comparableCount: number;
  bookingCount: number;
  /**
   * Number of realized booking comparables that fed the blend after currency
   * filtering and outlier removal. 0 when no realized-price evidence was
   * available (in which case behaviour is identical to pre-#1590). Distinct
   * from `bookingCount`, which is the count of completed trips for **this
   * specific car** used as the demand-multiplier signal.
   */
  realizedBookingCount: number;
  /**
   * Median realized daily rate from comparable past bookings, in the local
   * currency. `null` when there isn't enough realized-price evidence
   * (`< 3` samples) to compute a meaningful median.
   */
  realizedMedian: number | null;
  apexScore: number;
}

export interface ValuationContext {
  insuredValue?: number;
  pricingFieldsInPayload?: ReadonlySet<string>;
  currencySymbol?: string;
  countryCode?: string | null;
  /**
   * Full {@link SuggestedPricingInput} captured at the same time as the
   * AI-route suggestion so the validator's bounds match the host-facing
   * recommendation **exactly**. When supplied the validator delegates to
   * `computeSuggestedPricing(pricingInput)`; when absent it falls back to
   * the lighter `{ insuredValue, countryCode }` form below. Task #1540.
   */
  pricingInput?: SuggestedPricingInput;
  /**
   * Pre-computed pricing snapshot. Highest-priority source — when present
   * the validator skips re-computation and uses these bounds directly,
   * guaranteeing parity with whatever `/ai-enrichment/suggested-rate`
   * returned for the same listing.
   */
  precomputedPricing?: SuggestedPricing;
}

/**
 * Subset of car/listing fields used for validation. Keys are the canonical
 * column names; aliases are normalised by `normaliseListingFields`.
 *
 * Photo count is passed separately because it's stored in `cars_images`, not
 * on the `cars` row.
 */
export interface ListingFields {
  make?: string | null;
  model?: string | null;
  year?: number | string | null;
  color?: string | null;
  /** Some payloads send `colour_name` after a join. */
  colour_name?: string | null;
  mileage?: number | string | null;
  condition?: number | string | null;
  city?: string | null;
  state?: string | null;
  country_code?: string | null;
  description?: string | null;

  // Rental type toggles + their conditional price fields.
  rental_driving_active?: boolean | null;
  price_driving?: number | string | null;
  rental_chauffeured_active?: boolean | null;
  chauffeured_hourly_rate?: number | string | null;
  chauffeured_min_price_hourly_rate?: number | string | null;
  rental_event_active?: boolean | null;
  price_event?: number | string | null;
  event_hourly_rate?: number | string | null;
  event_min_price_hourly_rate?: number | string | null;

  // Delivery toggle + its conditional fields.
  delivery_enabled?: boolean | null;
  /** Self-drive-specific delivery availability; delivery_enabled is the aggregate capability. */
  self_drive_delivery_enabled?: boolean | null;
  delivery_free?: boolean | null;
  delivery_fee_per_unit?: number | string | null;
  delivery_radius_full?: number | string | null;
  delivery_maximum_distance?: number | string | null;
  delivery_radius_free?: number | string | null;

  // Mileage limit toggle + its conditional fields.
  mileage_limit?: boolean | null;
  mileage_limit_miles?: number | string | null;
  /** Canonical alias persisted in DB. */
  mileage_limit_per_day?: number | string | null;
  mileage_overage_rate?: number | string | null;
  overage_rate_per_mile?: number | string | null;

  // Instant book toggle + mode.
  instant_rental?: boolean | null;
  instant_book_mode?: "all" | "selected" | "off" | null;
}

/** Extra non-column inputs needed to evaluate conditional rules. */
export interface ConditionalContext {
  /** Number of event_type_cars rows attached to the listing. */
  eventTypeCount?: number;
  /** Number of car_instant_book_dates rows attached to the listing. */
  instantBookDateCount?: number;
}

/**
 * Optional draft-mode scoping for validateConditional.
 *
 * Task #1584: when the API merges a request payload onto an existing draft
 * row before validation, every conditional rule whose controlling toggle
 * lives on the DB row will fire even though the host never touched it on
 * Save & Exit from an earlier wizard step. Passing `draftFieldsInPayload`
 * makes the validator skip any rule whose controlling toggle field is NOT
 * present in the payload set, mirroring the existing
 * `pricingFieldsInPayload` pattern used by the above-ceiling guards.
 */
export interface ValidateConditionalOptions {
  draftFieldsInPayload?: ReadonlySet<string>;
}

/**
 * Host-side publish-readiness requirements (task #1007). Currently a single
 * "the host's phone number must be verified" gate, but defined as a context
 * object so future requirements (verified email, verified payout account,
 * etc.) can be added without breaking the call signature.
 *
 * The gate is toggled per-platform via the `host_publish_requirements`
 * platform_config row (default: requires_phone = true). Only the publish
 * surfaces (PUT /listings/:id/submit and PUT /listings/:id/pause when
 * transitioning inactive→active) pass this context — plain saves of
 * already-active listings deliberately do NOT pass it, so existing live
 * listings whose hosts pre-date the gate are not retroactively blocked from
 * editing.
 */
export interface HostPublishContext {
  /** Whether the platform requires a verified phone for publishing. */
  requirePhoneVerified?: boolean;
  /** The host's current `users.phone_verified` flag. */
  hostPhoneVerified?: boolean;
}
